import { create } from 'zustand';
import { ActionEntityType, ExportTemplateResponse, getAllExportTemplates } from '@pmp/api';

type ExportTemplatesStore = {
  isLoadingExportTemplates: boolean;
  getExportTemplates: (entityType: ActionEntityType) => Promise<ExportTemplateResponse[]>;
  getAllExportTemplates: () => Promise<ExportTemplateResponse[]>;
};

// The BFF default template is not created by the user, so it is surfaced first in every template list.
const orderTemplatesForSelection = (templates: ExportTemplateResponse[]): ExportTemplateResponse[] =>
  [...templates].sort((a, b) => Number(b.isDefaultTemplate) - Number(a.isDefaultTemplate));

export const useExportTemplatesStore = create<ExportTemplatesStore>((set, get) => {
  let exportTemplates: Record<string, ExportTemplateResponse[]> = {};
  let allExportTemplates: ExportTemplateResponse[] = [];

  return {
    isLoadingExportTemplates: false,

    getAllExportTemplates: async () => {
      set({ isLoadingExportTemplates: true });

      try {
        const { data } = await getAllExportTemplates();

        exportTemplates = data.reduce(
          (acc, template) => {
            acc[template.entityType.key] = acc[template.entityType.key] || [];
            acc[template.entityType.key].push(template);
            return acc;
          },
          {} as Record<string, ExportTemplateResponse[]>,
        );

        // Order each entity's templates with the system/default template ("Export to Import") first,
        // then user templates alphabetically. enso's export selectors render in this order.
        Object.values(exportTemplates).forEach(list =>
          list.sort((a, b) => Number(b.isDefaultTemplate) - Number(a.isDefaultTemplate) || a.name.localeCompare(b.name)),
        );

        allExportTemplates = data;
        return allExportTemplates;
      } catch (error) {
        console.error('Failed to load export templates:', error);
        throw error;
      } finally {
        set({ isLoadingExportTemplates: false });
      }
    },

    getExportTemplates: async (entityType: ActionEntityType) => {
      if (exportTemplates[entityType]) {
        return orderTemplatesForSelection(exportTemplates[entityType]);
      }

      await get().getAllExportTemplates();
      return orderTemplatesForSelection(exportTemplates[entityType] || []);
    },
  };
});
